import type { StateCreator } from 'zustand';
import type {
  VaultItem,
  VaultStats,
  VaultFilters,
  VaultQueueStatus,
  TensionCategory,
  VaultAnalysisSSEEvent,
  VaultQueueSSEEvent,
} from '../types';
import { vaultApi } from '../services/vaultApi';

let _articleDiscoveryAbort: AbortController | null = null;

export interface VaultArticleDiscovery {
  count: number;
  truncated: boolean;
  sample: { url: string; title: string | null }[];
  article_urls: string[];
  estimated_cost_usd: number;
  used_apify_probe: boolean;
  probe_cost_usd: number | null;
}

export interface VaultSlice {
  vaultItems: VaultItem[];
  vaultTotal: number;
  vaultStats: VaultStats | null;
  vaultSelectedItemId: number | null;
  vaultFilters: VaultFilters;
  vaultLoading: boolean;
  vaultQueue: VaultQueueStatus;
  vaultTensions: TensionCategory[];
  vaultBatchProgress: { total: number; completed: number } | null;
  /** Repost-Huerde dieses Kontos. 0 = aus. */
  vaultRepostMinScore: number;
  /** Vorschau "wie viele Artikel, was kostet's" fuer den Mehrfach-Import. */
  vaultArticleDiscovery: VaultArticleDiscovery | null;
  vaultArticleDiscoveryLoading: boolean;
  vaultArticleImportResult: { neu: number; bekannt: number; uebersprungen: number } | null;
  vaultArticleImportLoading: boolean;

  fetchVaultItems: () => Promise<void>;
  addToVault: (postId: number, strategyId?: number) => Promise<{ item_id?: number; existing?: boolean }>;
  addToVaultByUrl: (url: string, strategyId?: number) => Promise<{ item_id?: number; existing?: boolean; needs_scrape?: boolean; url?: string }>;
  scrapeAndAddToVault: (url: string, strategyId?: number) => Promise<{ item_id: number }>;
  discoverVaultArticles: (url: string) => Promise<void>;
  clearVaultArticleDiscovery: () => void;
  importVaultArticles: (articleUrls: string[], strategyId?: number) => Promise<void>;
  updateVaultItem: (itemId: number, updates: Partial<VaultItem>) => Promise<void>;
  deleteVaultItem: (itemId: number) => Promise<void>;
  reanalyzeVaultItem: (itemId: number) => Promise<void>;
  selectVaultItem: (itemId: number | null) => void;
  setVaultFilters: (partial: Partial<VaultFilters>) => void;
  fetchVaultStats: () => Promise<void>;
  fetchTensions: () => Promise<void>;
  fetchVaultSettings: () => Promise<void>;
  setVaultRepostMinScore: (value: number) => Promise<void>;
  handleVaultSSE: (event: VaultAnalysisSSEEvent | VaultQueueSSEEvent) => void;
}

const DEFAULT_FILTERS: VaultFilters = {
  sort: 'newest',
  limit: 50,
  offset: 0,
};

export const createVaultSlice: StateCreator<VaultSlice, [], [], VaultSlice> = (set, get) => ({
  vaultItems: [],
  vaultTotal: 0,
  vaultStats: null,
  vaultSelectedItemId: null,
  vaultFilters: DEFAULT_FILTERS,
  vaultLoading: false,
  vaultQueue: { queue_size: 0, analyzing_item_id: null },
  vaultTensions: [],
  vaultBatchProgress: null,
  vaultRepostMinScore: 0,
  vaultArticleDiscovery: null,
  vaultArticleDiscoveryLoading: false,
  vaultArticleImportResult: null,
  vaultArticleImportLoading: false,

  fetchVaultItems: async () => {
    set({ vaultLoading: true });
    try {
      const result = await vaultApi.listItems(get().vaultFilters);
      set({ vaultItems: result.items, vaultTotal: result.total });
    } finally {
      set({ vaultLoading: false });
    }
  },

  addToVault: async (postId, strategyId) => {
    const result = await vaultApi.addByPostId(postId, strategyId);
    if (!result.existing) {
      get().fetchVaultItems();
    }
    return result;
  },

  addToVaultByUrl: async (url, strategyId) => {
    return vaultApi.addByUrl(url, strategyId);
  },

  scrapeAndAddToVault: async (url, strategyId) => {
    const result = await vaultApi.scrapeAndAdd(url, strategyId);
    get().fetchVaultItems();
    return result;
  },

  // Vorschau vor dem Mehrfach-Import: bricht eine laufende Anfrage ab, sobald eine
  // neuere kommt, damit schnelles Tippen keine veraltete Antwort mehr zeigen kann
  // (gleiches Muster wie fetchScrapePreview in leadGenSlice.ts).
  discoverVaultArticles: async (url) => {
    _articleDiscoveryAbort?.abort();
    const ctrl = new AbortController();
    _articleDiscoveryAbort = ctrl;
    set({ vaultArticleDiscoveryLoading: true, vaultArticleImportResult: null });
    try {
      const res = await vaultApi.discoverArticles(url, ctrl.signal);
      if (_articleDiscoveryAbort === ctrl) {
        set({ vaultArticleDiscovery: res, vaultArticleDiscoveryLoading: false });
      }
    } catch (e) {
      if ((e as { name?: string })?.name === 'AbortError') return;
      if (_articleDiscoveryAbort === ctrl) {
        set({ vaultArticleDiscovery: null, vaultArticleDiscoveryLoading: false });
      }
    }
  },

  clearVaultArticleDiscovery: () => {
    _articleDiscoveryAbort?.abort();
    set({
      vaultArticleDiscovery: null,
      vaultArticleDiscoveryLoading: false,
      vaultArticleImportResult: null,
    });
  },

  importVaultArticles: async (articleUrls, strategyId) => {
    set({ vaultArticleImportLoading: true });
    try {
      const result = await vaultApi.importArticles(articleUrls, strategyId);
      set({ vaultArticleImportResult: result, vaultArticleImportLoading: false });
      get().fetchVaultItems();
    } catch (e) {
      set({ vaultArticleImportLoading: false });
      throw e;
    }
  },

  updateVaultItem: async (itemId, updates) => {
    const updated = await vaultApi.updateItem(itemId, updates);
    set((s) => ({
      vaultItems: s.vaultItems.map((i) => (i.id === itemId ? updated : i)),
    }));
  },

  deleteVaultItem: async (itemId) => {
    await vaultApi.deleteItem(itemId);
    set((s) => ({
      vaultItems: s.vaultItems.filter((i) => i.id !== itemId),
      vaultTotal: s.vaultTotal - 1,
      vaultSelectedItemId: s.vaultSelectedItemId === itemId ? null : s.vaultSelectedItemId,
    }));
  },

  reanalyzeVaultItem: async (itemId) => {
    await vaultApi.reanalyze(itemId);
    set((s) => ({
      vaultItems: s.vaultItems.map((i) =>
        i.id === itemId ? { ...i, analysis_status: 'queued' as const } : i
      ),
    }));
  },

  selectVaultItem: (itemId) => set({ vaultSelectedItemId: itemId }),

  setVaultFilters: (partial) => {
    set((s) => ({
      vaultFilters: { ...s.vaultFilters, ...partial, offset: 0 },
    }));
    get().fetchVaultItems();
  },

  fetchVaultStats: async () => {
    const stats = await vaultApi.getStats();
    set({ vaultStats: stats });
  },

  fetchVaultSettings: async () => {
    try {
      const res = await vaultApi.getSettings();
      set({ vaultRepostMinScore: res.repost_min_viral_score ?? 0 });
    } catch {
      // Einstellung ist eine Bequemlichkeit, kein Muss — 0 heisst ohnehin "Riegel aus".
    }
  },

  setVaultRepostMinScore: async (value) => {
    const clamped = Math.max(0, Number.isFinite(value) ? value : 0);
    set({ vaultRepostMinScore: clamped }); // optimistisch, damit das Feld nicht springt
    try {
      const res = await vaultApi.updateSettings({ repost_min_viral_score: clamped });
      set({ vaultRepostMinScore: res.repost_min_viral_score ?? clamped });
    } catch {
      await get().fetchVaultSettings();
    }
  },

  fetchTensions: async () => {
    const tensions = await vaultApi.getTensions();
    set({ vaultTensions: tensions });
  },

  handleVaultSSE: (event) => {
    if (event.event === 'vault_analysis_complete') {
      const evt = event as VaultAnalysisSSEEvent;
      set((s) => {
        const updatedItems = s.vaultItems.map((item) => {
          if (item.id === evt.item_id) {
            return { ...item, analysis_status: evt.status };
          }
          return item;
        });

        const completed = (s.vaultBatchProgress?.completed ?? 0) + 1;
        const total = s.vaultBatchProgress?.total ?? completed;
        const newProgress = total > 1 ? { total, completed } : null;

        return { vaultItems: updatedItems, vaultBatchProgress: newProgress };
      });

      // Reload the updated item to get full analysis
      if (evt.status === 'succeeded') {
        vaultApi.getItem(evt.item_id).then((updated) => {
          set((s) => ({
            vaultItems: s.vaultItems.map((i) => (i.id === evt.item_id ? updated : i)),
          }));
        }).catch(() => {});
      }
    } else if (event.event === 'vault_queue_update') {
      const evt = event as VaultQueueSSEEvent;
      set((s) => {
        const queue: VaultQueueStatus = {
          queue_size: evt.queue_size,
          analyzing_item_id: evt.analyzing_item_id ?? null,
        };

        // Initialize batch progress when queue grows
        const prevQueue = s.vaultQueue.queue_size;
        let batchProgress = s.vaultBatchProgress;
        if (evt.queue_size > 0 && prevQueue === 0) {
          batchProgress = { total: evt.queue_size, completed: 0 };
        } else if (evt.queue_size === 0) {
          batchProgress = null;
        }

        return { vaultQueue: queue, vaultBatchProgress: batchProgress };
      });
    }
  },
});
